import { useMemo, useState, useSyncExternalStore } from "react";
import { useChatModels, WORKSHOP_AUTO_MODEL_ID } from "@/hooks/useChatModels";
import { useGetBrands } from "@/hooks/useGetBrands";
import { useAuthStore } from "@/stores/authStore";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { useAgentRuns, useBrandIdentity, useUniverse } from "../data/useCreativeOsHome";
import { useCalendarPlan, useCalendarPlans } from "../content-calendar/api/useContentCalendar";
import { stageOf } from "../content-calendar/month/contentStage";
import { useMarketSignals } from "../market-signals/api/useMarketSignals";
import { useFieldReports } from "../market-signals/api/useFieldReports";
import { useOrganizationMembers } from "../settings/api/useOrganization";
import { useSocialConnections } from "../settings/api/useSocialConnections";
import { useTrends, type TrendFilters } from "../trends/api/useTrends";
import { platformLabel, recreatePrompt, TYPE_LABEL } from "../trends/components/trendMeta";
import { useWorkshopProjects } from "../workshop/api/useWorkshopProjects";
import { useWorkshopQuickCreate } from "../workshop/api/useWorkshopQuickCreate";
import { useLastVisit } from "./lastVisit";
import {
  latest,
  learned,
  liveEvents,
  monthRow,
  needsYou,
  postsLiveSince,
  sinceLabel,
  standingLine,
} from "./homeSince";
import type { HomeCore, HomeData, HomeState } from "./home.types";

/**
 * The live Home read. Every figure comes from a query that can fail on its own,
 * so a figure is `null` while unread and never a stand-in 0.
 */

/** The trends route's own ranking; one row is the trend of the week. */
const TREND_FILTERS: TrendFilters = { pageSize: 1 };
const FIELD_REPORTER = "field_reporter";

/* One minute clock for the whole app (ages read "9 h" and tick on their own). */
let clockNow = Date.now();
const clockSubscribers = new Set<() => void>();
let clockTimer: number | null = null;
function subscribeToClock(onChange: () => void): () => void {
  clockNow = Date.now();
  clockSubscribers.add(onChange);
  if (clockTimer === null) {
    clockTimer = window.setInterval(() => {
      clockNow = Date.now();
      for (const notify of clockSubscribers) notify();
    }, 60_000);
  }
  return () => {
    clockSubscribers.delete(onChange);
    if (clockSubscribers.size === 0 && clockTimer !== null) {
      window.clearInterval(clockTimer);
      clockTimer = null;
    }
  };
}
const readClock = () => clockNow;
const useMinuteClock = () => useSyncExternalStore(subscribeToClock, readClock, readClock);

function shiftMonth(year: number, month: number, by: number) {
  const d = new Date(year, month - 1 + by, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

export function useHomeData(options: { sinceOverride?: number | null } = {}): { data: HomeData; state: HomeState } {
  const identity = useBrandIdentity();
  const brandId = identity.id || null;
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const orgId = useWorkspaceStore((s) => s.selectedOrganizationId);
  const visit = useLastVisit(userId, brandId, options.sinceOverride);
  const now = useMinuteClock();

  const universe = useUniverse();
  const runs = useAgentRuns();
  const signals = useMarketSignals(brandId);
  const reports = useFieldReports(brandId);
  const members = useOrganizationMembers(orgId);
  const connections = useSocialConnections(brandId);
  const trends = useTrends(TREND_FILTERS);
  const brands = useGetBrands();
  /* The composer's own reads, started here so it mounts with them in hand. */
  const projects = useWorkshopProjects(brandId);
  const quickCreate = useWorkshopQuickCreate(brandId);
  const models = useChatModels({ autoModelId: WORKSHOP_AUTO_MODEL_ID });

  /* Three months of the calendar: this one (the strip, the counters), the one
     before (the strip, Latest) and the next (sends and picks already open on it). */
  const ym = useMemo(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  }, []);
  const plans = useCalendarPlans(brandId);
  const rowOf = (by: number) => {
    const at = shiftMonth(ym.year, ym.month, by);
    return plans.data?.find((p) => p.year === at.year && p.month === at.month) ?? null;
  };
  const currentRow = rowOf(0);
  const previousRow = rowOf(-1);
  const nextRow = rowOf(1);
  const current = useCalendarPlan(currentRow?.id ?? null);
  const previous = useCalendarPlan(previousRow?.id ?? null);
  const next = useCalendarPlan(nextRow?.id ?? null);

  const data = useMemo<HomeData>(() => {
    const brandName = identity.name;
    const since = visit.since;
    const loaded = [current.data, previous.data, next.data];
    const memberList = members.data ?? [];
    const nameOf = (id: string | null) => (id ? (memberList.find((m) => m.userId === id)?.name ?? null) : null);

    /* -------------------------------------------------------- read flags */
    const plansRead = !!brandId && !plans.isLoading && !plans.isError;
    const currentRead = plansRead && (!currentRow || (!current.isLoading && !current.isError));
    const previousRead = plansRead && (!previousRow || (!previous.isLoading && !previous.isError));
    const universeRead = !!brandId && !universe.isLoading && !universe.isError;
    const runsRead = !!brandId && !runs.isLoading && !runs.isError;
    const signalsRead = !!brandId && !signals.isLoading && !signals.isError;
    const reportsRead = !!brandId && !reports.isLoading && !reports.isError;
    const connectionsRead = !!brandId && !connections.isLoading && !connections.isError;

    /* ------------------------------------------------------------ figures */
    const live = liveEvents(loaded);
    const postsSince = currentRead && previousRead ? postsLiveSince(live, since) : null;
    const factsNewIds = new Set(
      universe.facts.filter((f) => Date.parse(f.learnedAt) > since).map((f) => f.id),
    );
    const factsNew = universeRead ? factsNewIds.size : null;
    const reportsSince = runsRead
      ? runs.runs.filter((r) => r.agentType === FIELD_REPORTER && r.finishedAt && Date.parse(r.finishedAt) > since).length
      : null;

    const calls = needsYou(loaded, connections.data ?? [], brandName, now);
    const needsRead = currentRead && connectionsRead;
    const needCount = needsRead ? calls.length : null;

    const liveThisMonth = currentRead
      ? (current.data?.slots ?? []).filter((s) => stageOf(s) === "posted").length
      : null;

    const sinceWord = sinceLabel(since, now, visit.firstVisit);
    const line =
      postsSince === null && factsNew === null && reportsSince === null && needCount === null
        ? null
        : standingLine(
            { posts: postsSince, facts: factsNew, reports: reportsSince, needs: needCount },
            visit.firstVisit ? "this week" : sinceWord.toLowerCase(),
          );

    /* ------------------------------------------------------- brand core */
    const core: HomeCore | null = universeRead
      ? {
          rings: universe.rings.map((r) => ({
            key: r.id,
            arcs: r.arcs.map((a) => ({ start: a.start, end: a.end, charted: a.charted })),
          })),
          dots: [
            ...universe.facts
              .filter((f) => f.ring >= 0)
              .map((f) => ({
                id: f.id,
                ring: f.ring,
                angle: f.ang0,
                kind: factsNewIds.has(f.id) ? ("new" as const) : ("fact" as const),
              })),
            ...universe.rings.flatMap((r, i) =>
              r.echoes.map((e) => ({ id: `echo:${e.factId}:${e.arcKey}`, ring: i, angle: e.ang0, kind: "echo" as const })),
            ),
          ],
          total: universe.total,
          newCount: factsNewIds.size,
        }
      : null;

    /* ------------------------------------------------------------- months */
    const months = [
      monthRow("current", ym.month - 1, current.data ?? null, currentRow?.status ?? null, brandName, now),
    ];
    if (previousRow) {
      const at = shiftMonth(ym.year, ym.month, -1);
      months.push(monthRow("previous", at.month - 1, previous.data ?? null, previousRow.status, brandName, now));
    }

    const top = trends.trends[0] ?? null;
    const trendRead = !trends.isError && !trends.isLoading;

    return {
      brand: { id: brandId ?? "", name: brandName, initial: identity.initial },
      standing: { sinceLabel: sinceWord, line },
      counters: {
        liveThisMonth,
        needYou: needCount,
        running: runsRead ? runs.running.length : null,
        facts: universeRead ? universe.total : null,
        factsNew,
      },
      core,
      needs: { items: calls, total: needCount },
      months,
      monthsRead: currentRead && previousRead,
      learned: learned(universeRead ? universe.facts : [], signals.signals, nameOf, now),
      learnedRead: universeRead || signalsRead,
      latest: latest({
        plans: loaded,
        live,
        reports: reports.reports,
        signals: signals.signals,
        brandName,
        nameOf,
        now,
      }),
      latestRead: currentRead || reportsRead,
      trend:
        trendRead && top
          ? {
              id: top.id,
              title: top.title,
              meta: [TYPE_LABEL[top.type], top.platforms.map(platformLabel).join(", ")]
                .filter(Boolean)
                .join(" · ")
                .toUpperCase(),
              body: top.body,
              videoUrl: top.previewVideo && !top.previewVideo.videoBroken ? top.previewVideo.videoUrl : null,
              href: `/dashboard/trends?trend=${encodeURIComponent(top.id)}`,
              recreatePrompt: recreatePrompt(top),
            }
          : null,
      trendRead,
    };
  }, [
    identity.name,
    identity.initial,
    brandId,
    visit.since,
    visit.firstVisit,
    now,
    ym,
    plans.isLoading,
    plans.isError,
    currentRow,
    previousRow,
    current.data,
    current.isLoading,
    current.isError,
    previous.data,
    previous.isLoading,
    previous.isError,
    next.data,
    members.data,
    universe.facts,
    universe.rings,
    universe.total,
    universe.isLoading,
    universe.isError,
    runs.runs,
    runs.running,
    runs.isLoading,
    runs.isError,
    signals.signals,
    signals.isLoading,
    signals.isError,
    reports.reports,
    reports.isLoading,
    reports.isError,
    connections.data,
    connections.isLoading,
    connections.isError,
    trends.trends,
    trends.isLoading,
    trends.isError,
  ]);

  /* Every read on the page, the composer's included, answers (or fails) before
     anything is drawn; until then the page is one skeleton. No brand selected
     yet but the list has some: the brand picker is about to pick one. */
  const brandKnown = !!brandId && !!brands.data?.some((b) => b.id === brandId);
  const settling =
    brands.isLoading ||
    (!brandKnown && !!brands.data?.length) ||
    (!!brandId &&
      (plans.isLoading ||
        (!!currentRow && current.isLoading) ||
        (!!previousRow && previous.isLoading) ||
        (!!nextRow && next.isLoading) ||
        universe.isLoading ||
        runs.isLoading ||
        signals.isLoading ||
        reports.isLoading ||
        members.isLoading ||
        connections.isLoading ||
        trends.isLoading ||
        projects.isLoading ||
        quickCreate.isLoading ||
        models.isLoading));
  /* Once a brand's page has drawn it stays drawn: a read that starts later
     (next month's plan appearing) fills in, it never blanks the page again. */
  const [drawnFor, setDrawnFor] = useState<string | null>(null);
  if (!settling && brandId && drawnFor !== brandId) setDrawnFor(brandId);

  return {
    data,
    state: {
      isLoading: brandId ? drawnFor !== brandId : settling,
      // Only the calendar failing takes the page down; every other read is one region.
      isError: plans.isError,
    },
  };
}
